import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import { serviceFaqs } from "@/data/serviceFaqs";
import img from "@/assets/whole-home-video.jpg";

const bullets = [
  "4K HDR matrix switching (HDBaseT and AV-over-IP)",
  "Centralized rack with hidden source equipment",
  "Watch any source on any TV, any time",
  "Sports bar mode — multiple games, multiple screens",
  "Integrated with your automation and audio platforms",
  "Whisper-quiet equipment cooling and cable management",
];

export const Route = createFileRoute("/services/whole-home-video")({
  head: () => ({
    meta: [
      { title: "Whole-Home Video Distribution Install | Apex AV Denver" },
      { name: "description", content: "Distribute 4K HDR video to every TV in your Denver home from a single rack. Matrix switching, hidden source equipment, and unified control." },
      
      { name: "keywords", content: "whole-home video Denver, video distribution Denver, 4K video distribution Denver, AV over IP Denver, HDBaseT installation Denver, matrix switch installation Denver, centralized video rack Denver, hidden cable box installation Denver, multi-room TV system Denver, distributed video Denver" },
      { property: "og:title", content: "Whole-Home Video Distribution — Apex AV Denver" },
      { property: "og:description", content: "One rack, every TV. Matrix 4K HDR video distribution in Denver." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/whole-home-video" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Whole-Home Video Distribution — Apex AV Denver" },
      { name: "twitter:description", content: "One rack, every TV. Matrix 4K HDR video distribution in Denver." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/whole-home-video" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "whole-home-video",
            name: "Whole-Home Video Distribution",
            description: "4K HDR matrix video distribution to every TV in Denver homes.",
            pageTitle: "Whole-Home Video Distribution Install | Apex AV Denver",
            pageDescription: "Distribute 4K HDR video to every TV in your Denver home from a single rack. Matrix switching, hidden source equipment, and unified control.",
            image: img,
            faqs: serviceFaqs["whole-home-video"],
            serviceType: "Whole-Home Video Distribution",
            breadcrumbName: "Whole-Home Video Distribution Denver",
            offerCatalog: bullets,
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      topics={["av-installation", "whole-home-audio", "home-theater", "networking"]}
      eyebrow="Whole-home video"
      title="Every TV in the house. One rack of gear."
      intro={<>Centralize your cable boxes and streaming devices in one closet — and distribute crisp 4K HDR video to every TV with <strong>whole-home video distribution in Denver</strong>, <strong>AV-over-IP</strong>, and clean hidden source equipment.</>}
      image={img}
      imageAlt="Whole-home 4K video distribution matrix installation in a Highlands Ranch Denver residence"
      brands={["sony", "lg", "samsung", "marantz", "denon"]}
      bullets={bullets}
      features={[
        { title: "Clean rooms", desc: "No cable boxes or wires at any TV — just the screen on the wall." },
        { title: "One remote", desc: "Same interface in every room, your kids actually understand it." },
        { title: "Future-proof", desc: "8K-ready cabling and modular hardware that scales with you." },
      ]}
      faqs={serviceFaqs["whole-home-video"]}
    />
  ),
});
